import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Car, CheckCircle, Clock, Link2, Wrench } from 'lucide-react';
import axios from 'axios';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert } from '@/components/ui/alert';
import { EmptyState } from '@/components/empty-state/EmptyState';
import { cn } from '@/lib/utils';

type PortalVeiculo = {
  placa: string;
  modelo: string | null;
  marca: string | null;
  ano: number | null;
  cor: string | null;
  kmAtual: number | null;
  proprietario: string | null;
};

type PortalOs = {
  id: string;
  numeroOs: string;
  status: string;
  dataAbertura: string;
  dataFechamento: string | null;
  mecanico: string | null;
  servicos: { descricao: string; quantidade: number }[];
  pecas: { descricao: string; quantidade: number }[];
  observacoes: string | null;
};

type PortalResponse = { veiculo: PortalVeiculo; historico: PortalOs[] };

const STATUS_LABEL: Record<string, { label: string; color: string }> = {
  ABERTA: { label: 'Aberta', color: 'bg-blue-100 text-blue-800' },
  EM_ANDAMENTO: { label: 'Em andamento', color: 'bg-yellow-100 text-yellow-800' },
  AGUARDANDO_PECAS: { label: 'Aguardando peças', color: 'bg-orange-100 text-orange-800' },
  FINALIZADA: { label: 'Concluída', color: 'bg-green-100 text-green-800' },
  PAGA: { label: 'Paga', color: 'bg-green-100 text-green-800' },
};

function apiBaseUrl() {
  const raw = import.meta.env.VITE_API_URL?.trim();
  if (!raw) return '/api/v1';
  const base = raw.replace(/\/$/, '');
  if (base.endsWith('/api/v1')) return base;
  return `${base}/api/v1`;
}

/**
 * Fase 19 (Plano 19-08), achado C-03/D-03c: o único caminho de acesso a
 * esta página passa a ser um link completo (`?placa=X&token=Y`) enviado
 * pela oficina — a caixa de busca manual por placa foi REMOVIDA porque era
 * o mesmo vetor do achado C-03 em forma de tela (qualquer visitante podia
 * digitar qualquer placa, sem token, e o back-end antigo devolvia o
 * histórico). Sem os dois parâmetros na URL, a página mostra um estado
 * vazio orientando a usar o link enviado pela oficina.
 *
 * Exceção explícita de lazy loading (CLAUDE.md §2.2): esta página, junto
 * com LoginPage e RedefinirSenhaInicialPage, entra em App.tsx com import
 * estático.
 */
export function PortalClientePage() {
  const [searchParams] = useSearchParams();
  const placaParam = searchParams.get('placa')?.trim().toUpperCase() ?? '';
  const tokenParam = searchParams.get('token')?.trim() ?? '';
  const linkCompleto = Boolean(placaParam && tokenParam);

  const q = useQuery<PortalResponse>({
    queryKey: ['portal-veiculo', placaParam, tokenParam],
    queryFn: () =>
      axios
        .get(`${apiBaseUrl()}/portal/veiculo`, { params: { placa: placaParam, token: tokenParam } })
        .then((r) => r.data),
    enabled: linkCompleto,
    retry: false,
  });

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <div className="mx-auto max-w-2xl px-4 py-12">
        {/* Header */}
        <div className="mb-10 text-center">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-primary/10">
            <Car className="h-7 w-7 text-primary" />
          </div>
          <h1 className="text-2xl font-bold tracking-tight">Histórico do seu veículo</h1>
          <p className="mt-2 text-muted-foreground">
            Acompanhe aqui o histórico de serviços realizados no seu veículo.
          </p>
        </div>

        {!linkCompleto && (
          <div className="animate-in fade-in duration-200">
            <EmptyState
              icon={Link2}
              title="Acesse pelo link enviado pela sua oficina"
              description="Não encontramos um link válido nesta página. Se o link que você recebeu não abrir o histórico, peça à oficina para enviar um novo."
            />
          </div>
        )}

        {linkCompleto && q.isFetching && (
          <div className="mt-8 space-y-4">
            <Skeleton className="h-24 w-full rounded-xl" />
            <Skeleton className="h-40 w-full rounded-xl" />
          </div>
        )}

        {linkCompleto && q.isError && (
          <Alert variant="error" className="mt-6 animate-in fade-in duration-200">
            Não foi possível abrir este histórico. Peça à sua oficina um link novo.
          </Alert>
        )}

        {q.data && (
          <div className="mt-8 space-y-6">
            {/* Vehicle card */}
            <div className="rounded-xl border bg-card p-5 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
                  <Car className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="font-os text-lg font-bold">{q.data.veiculo.placa}</p>
                  <p className="text-sm text-muted-foreground">
                    {[q.data.veiculo.marca, q.data.veiculo.modelo, q.data.veiculo.ano].filter(Boolean).join(' · ')}
                    {q.data.veiculo.cor && ` · ${q.data.veiculo.cor}`}
                  </p>
                </div>
              </div>
              {q.data.veiculo.proprietario && (
                <p className="mt-3 text-sm text-muted-foreground">
                  Proprietário: <span className="font-medium text-foreground">{q.data.veiculo.proprietario}</span>
                </p>
              )}
              {q.data.veiculo.kmAtual != null && (
                <p className="mt-1 text-sm text-muted-foreground">
                  Última KM registrada: <span className="font-medium text-foreground">{q.data.veiculo.kmAtual.toLocaleString('pt-BR')} km</span>
                </p>
              )}
            </div>

            {/* History */}
            <div>
              <h2 className="mb-3 text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                {q.data.historico.length} atendimento(s)
              </h2>

              {q.data.historico.length === 0 ? (
                <div className="rounded-xl border border-dashed py-10 text-center text-muted-foreground">
                  Nenhum atendimento registrado.
                </div>
              ) : (
                <div className="space-y-4">
                  {q.data.historico.map((os) => {
                    const statusInfo = STATUS_LABEL[os.status] ?? { label: os.status, color: 'bg-gray-100 text-gray-800' };
                    const finalizada = ['FINALIZADA', 'PAGA'].includes(os.status);
                    return (
                      <div key={os.id} className="rounded-xl border bg-card shadow-sm">
                        <div className="flex items-start justify-between gap-3 p-4">
                          <div className="flex items-center gap-2">
                            {finalizada
                              ? <CheckCircle className="h-4 w-4 text-green-600" />
                              : <Clock className="h-4 w-4 text-yellow-600" />}
                            <span className="font-os font-semibold">{os.numeroOs}</span>
                          </div>
                          <span className={cn('rounded-full px-2 py-0.5 text-xs font-medium', statusInfo.color)}>
                            {statusInfo.label}
                          </span>
                        </div>

                        <div className="border-t px-4 pb-4 pt-3 space-y-3">
                          <div className="flex gap-4 text-xs text-muted-foreground">
                            <span>Abertura: {new Date(os.dataAbertura).toLocaleDateString('pt-BR')}</span>
                            {os.dataFechamento && (
                              <span>Conclusão: {new Date(os.dataFechamento).toLocaleDateString('pt-BR')}</span>
                            )}
                            {os.mecanico && <span>Mecânico: {os.mecanico}</span>}
                          </div>

                          {os.servicos.length > 0 && (
                            <div>
                              <p className="mb-1 flex items-center gap-1 text-xs font-semibold text-muted-foreground">
                                <Wrench className="h-3 w-3" /> Serviços
                              </p>
                              <ul className="space-y-0.5">
                                {os.servicos.map((s, i) => (
                                  <li key={i} className="text-sm">
                                    {s.descricao}
                                    {s.quantidade !== 1 && <span className="text-muted-foreground"> × {s.quantidade}</span>}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}

                          {os.pecas.length > 0 && (
                            <div>
                              <p className="mb-1 text-xs font-semibold text-muted-foreground">Peças / produtos</p>
                              <ul className="space-y-0.5">
                                {os.pecas.map((p, i) => (
                                  <li key={i} className="text-sm text-muted-foreground">
                                    {p.descricao}
                                    {p.quantidade !== 1 && ` × ${p.quantidade}`}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}

                          {os.observacoes && (
                            <p className="rounded-md bg-muted/50 px-3 py-2 text-xs text-muted-foreground italic">
                              {os.observacoes}
                            </p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
